import { DayPnlStrip } from "@/components/DayPnlStrip";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, num, rupee, pnlClass, dataReady, isStructure, STRATEGY_LABELS } from "@/lib/api";
import { StructureCard } from "@/components/StructureCard";
import { CandleChart } from "@/components/CandleChart";
import { AdvisorPanel } from "@/components/AdvisorPanel";
import { SignalChecklist } from "@/components/SignalChecklist";
import { OptionChainTable } from "@/components/OptionChainTable";
import { SpreadCard } from "@/components/SpreadCard";
import { Empty, DataAccessNotice } from "@/components/Stat";

const usePoll = (fn, ms, deps) => {
  useEffect(() => {
    fn();
    const id = setInterval(fn, ms);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};

export default function LiveTab({ status, reloadStatus }) {
  const connected = dataReady(status);
  const recheck = async () => {
    await api.post("/recheck").catch(() => {});
    reloadStatus();
  };
  const [live, setLive] = useState(null);
  const [chain, setChain] = useState(null);
  const [expiry, setExpiry] = useState(null);
  const [sug, setSug] = useState(null);
  const [forceDir, setForceDir] = useState(null);
  const [settings, setSettings] = useState(null);
  const [summary, setSummary] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)); }, []);

  const loadLive = useCallback(() => {
    if (!connected) return;
    api.get("/live").then((r) => { setLive(r.data); setErr(null); }).catch((e) => {
      setErr(errMsg(e));
      if (e?.response?.data?.subscription_required) reloadStatus();
    });
    api.get("/paper/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, [connected, reloadStatus]);
  usePoll(loadLive, 5000, [loadLive]);

  usePoll(() => {
    if (!connected) return;
    api.get("/option-chain", { params: expiry ? { expiry } : {} }).then((r) => setChain(r.data)).catch(() => {});
  }, 10000, [connected, expiry]);

  const verdict = live?.signal?.verdict;
  usePoll(() => {
    if (!connected) return;
    api.get("/suggestion", { params: forceDir ? { direction: forceDir } : {} })
      .then((r) => setSug(r.data)).catch((e) => setSug({ error: errMsg(e) }));
  }, 15000, [connected, forceDir, verdict]);

  const take = async () => {
    setBusy(true);
    try {
      const r = await api.post("/paper/trades", { direction: sug.legs ? "ENTER" : sug.direction });
      toast.success(`Paper ${r.data.strategy} opened at ${r.data.debit < 0 ? "credit" : "debit"} ${num(Math.abs(r.data.debit))}`);
      loadLive();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (status?.data_source === "dhan" && status?.data_access === false) return <DataAccessNotice onRecheck={recheck} />;
  if (!connected) return <Empty id="live-not-connected">Connect Dhan in Settings, or switch the data source to Free, to load NIFTY data, signals and the option chain.</Empty>;
  const p = settings?.strategy;
  const multi = isStructure(p?.strategy_type);
  const hl = sug && !sug.error ? (sug.legs ? sug.legs.map((l) => l.strike).filter((k) => k != null) : sug.buy_strike ? [sug.buy_strike, sug.sell_strike] : []) : [];

  return (
    <div className="grid grid-cols-12 gap-3 sm:gap-4 fade-in">
      <AdvisorPanel />
      <section className="col-span-12 panel p-3 lg:col-span-8">
        <div className="mb-2 flex flex-wrap items-end justify-between gap-2 px-1">
          <div>
            <div className="eyebrow">NIFTY 50 spot · {p?.interval || 5}m · {live?.session_date || "—"} · <span data-testid="live-source" className={status?.data_source === "free" ? "text-amber-400" : "text-slate-400"}>{status?.data_source === "free" ? "free data (Yahoo · NSE, may be delayed)" : "Dhan"}</span></div>
            <div data-testid="live-spot-price" className="num text-3xl font-bold tracking-tight">{num(live?.spot)}</div>
          </div>
          <div className="flex flex-wrap gap-3 text-[11px]">
            <Legend c="#8B5CF6" t="VWAP" /><Legend c="#3B82F6" t={`EMA ${p?.ema_fast || 20}`} />
            <Legend c="#F59E0B" t={`EMA ${p?.ema_slow || 50}`} /><Legend c="#6366F1" t="Opening range" dashed />
            {p?.strategy_type === "bb_rsi_vwap" && <Legend c="#14B8A6" t={`Bollinger ${p?.bb_period || 20}, ${p?.bb_std || 2}σ`} dashed />}
          </div>
        </div>
        <DayPnlStrip />
        {err && <p data-testid="live-error" className="mb-2 px-1 text-xs text-red-400">{err}</p>}
        <CandleChart candles={live?.candles || []} rsiLong={p?.strategy_type === "bb_rsi_vwap" ? p?.bb_rsi_high ?? 65 : p?.rsi_long ?? 55} rsiShort={p?.strategy_type === "bb_rsi_vwap" ? p?.bb_rsi_low ?? 35 : p?.rsi_short ?? 45} showBands={p?.strategy_type === "bb_rsi_vwap"} levels={live?.levels} markers={live?.markers} />
      </section>
      <aside className="col-span-12 flex flex-col gap-3 lg:col-span-4">
        <SignalChecklist signal={live?.signal} />
        <div className="panel grid grid-cols-2 gap-2 p-4 text-xs" data-testid="panel-today-risk">
          <div><div className="eyebrow">Today P&amp;L</div><div className={`num text-lg font-bold ${pnlClass(summary?.today_total)}`} data-testid="today-pnl">{rupee(summary?.today_total)}</div></div>
          <div><div className="eyebrow">Daily limit</div><div className="num text-lg font-bold text-slate-300">{rupee(-(summary?.daily_loss_limit || 0))}</div></div>
          <div className="col-span-2 text-slate-500">
            Auto-paper: <span className="text-slate-300" data-testid="auto-paper-state">{summary?.auto_paper ? "ON" : "OFF"}</span>
            {summary?.halted && <span className="ml-2 font-semibold text-red-400" data-testid="halted-flag">HALTED — daily limit hit</span>}
          </div>
        </div>
      </aside>
      <section className="col-span-12 lg:col-span-7">
        <OptionChainTable chain={chain} highlight={hl} expiry={expiry} onExpiry={setExpiry} />
      </section>
      <section className="col-span-12 lg:col-span-5">
        {multi ? (
          <StructureCard sug={sug} kind={p.strategy_type} label={STRATEGY_LABELS[p.strategy_type]} onTake={take} busy={busy} force={forceDir} onForce={setForceDir} risk={settings?.risk?.risk_per_trade} />
        ) : (
          <SpreadCard sug={sug} onTake={take} busy={busy} forceDir={forceDir} onForce={setForceDir} risk={settings?.risk?.risk_per_trade} />
        )}
      </section>
    </div>
  );
}

const Legend = ({ c, t, dashed }) => (
  <span className="flex items-center gap-1.5 text-slate-400">
    <span className="inline-block w-4" style={{ borderTop: `2px ${dashed ? "dashed" : "solid"} ${c}` }} />{t}
  </span>
);
